import { Component, OnInit } from '@angular/core';
import { PatientDashboardTabComponent } from 'src/app/patients/dashboard-page/dashboard-tab/dashboard-tab.component';
import { PatientDashboardStore } from 'src/app/patients/state/dashboard-page.store';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { Store } from '@ngrx/store';
import { PatientService } from 'src/app/calvin-emr-shared/services/patient.service';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { listAll } from 'firebase/storage';
import { arrayUnion, arrayRemove } from 'firebase/firestore';
import { ChangeDetectorRef } from '@angular/core';
import { NgIf, NgFor } from '@angular/common';
import { ButtonDirective } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

interface FirebaseStorageFile {
  name: string;
  url: string;
}

@Component({
    selector: 'app-upload-tab',
    templateUrl: './upload-tab.component.html',
    styleUrls: ['./upload-tab.component.scss'],
    standalone: true,
    imports: [
        NgIf,
        NgFor,
        ButtonDirective,
        TooltipModule,
    ],
})
export class UploadTabComponent extends PatientDashboardTabComponent implements OnInit {
  uploads: FirebaseStorageFile[] = [];
  notificationMessage: string | undefined;
  uploading: boolean = false;
  patient: Patient | undefined;
  fileSelected: boolean = false;

  constructor(
    patientStore: PatientDashboardStore,
    store: Store,
    private patientService: PatientService,
    private cdr: ChangeDetectorRef
  ) {
    super(patientStore, store);
    this.patient$.subscribe((patient: Patient) => {
      this.patient = patient;
      this.loadUploadedDocumentsFromLocalStorage(); // Load from localStorage
      this.loadUploadedDocumentsFromFirebase(); // Load from Firebase
    });
  }

  ngOnInit(): void {}

  onFileSelected(event: any): void {
    this.fileSelected = event.target.files && event.target.files.length > 0;
  }

  onDocumentUploaded(fileInput: HTMLInputElement): void {
    const files: FileList | null = fileInput.files;
    if (files && files.length > 0) {
      this.cdr.detectChanges();
      this.uploadPatientDocument(files[0]);
    }
  }

  uploadDocument(fileInput: HTMLInputElement): void {
    const files: FileList | null = fileInput.files;
    if (files && files.length > 0) {
      this.uploadPatientDocument(files[0]);
    }
  }

  uploadPatientDocument(document: File): void {
    const maxSizeInBytes = 100 * 1024 * 1024;

    this.uploading = true;

    const patientId = this.patient?.id;

    if (!patientId) {
      this.notificationMessage = 'Error: Patient ID not available.';
      this.uploading = false;
      return;
    }

    const storage = getStorage();
    const documentStorageRef = ref(storage, `${patientId}/${document.name}`);

    let documentUrl: string;

    uploadBytes(documentStorageRef, document)
      .then(() => getDownloadURL(documentStorageRef))
      .then((downloadUrl) => {
        documentUrl = downloadUrl;

        return this.patientService.updatePatientField(
          patientId,
          'uploadedDocuments',
          arrayUnion({ name: document.name, url: documentUrl })
        );
      })
      .then(() => {
        this.uploads.push({ name: document.name, url: documentUrl });

        // Store uploads in localStorage
        localStorage.setItem('uploads', JSON.stringify(this.uploads));

        this.notificationMessage = 'Document upload successful!';
      })
      .catch((error) => {
        console.error('Error uploading document:', error);
        this.notificationMessage = 'Error uploading document. Please try again.';
      })
      .finally(() => {
        this.uploading = false; // Hide loading indicator
      });
  }

  loadUploadedDocumentsFromFirebase(): void {
    const patientId = this.patient?.id;

    if (!patientId) {
      console.error('Error: Patient ID not available.');
      return;
    }

    const storage = getStorage();
    const patientStorageRef = ref(storage, `${patientId}/`);

    this.uploads = [];

    listAll(patientStorageRef)
      .then((result) => {
        result.items.forEach((item) => {
          getDownloadURL(item).then((url) => {
            this.uploads.push({ name: item.name, url: url });
          });
        });
      })
      .catch((error) => {
        console.error('Error fetching uploaded documents:', error);
      });
  }

  deleteUpload(document: FirebaseStorageFile): void {
    const patientId = this.patient?.id;

    if (!patientId) {
      this.notificationMessage = 'Error: Patient ID not available.';
      return;
    }

    const storage = getStorage();
    const documentStorageRef = ref(storage, `${patientId}/${document.name}`);

    // Delete document from Firebase Storage
    deleteObject(documentStorageRef)
      .then(() => {
        return this.patientService.updatePatientField(
          patientId,
          'uploadedDocuments',
          arrayRemove({ name: document.name, url: document.url })
        );
      })
      .then(() => {
        this.uploads = this.uploads.filter((upload) => upload !== document);

        localStorage.setItem('uploads', JSON.stringify(this.uploads));
      })
      .catch((error) => {
        console.error('Error deleting document:', error);
        this.notificationMessage = 'Error deleting document. Please try again.';
      });
  }

  loadUploadedDocumentsFromLocalStorage(): void {
    const uploadedDocuments = localStorage.getItem('uploads');
    if (uploadedDocuments) {
      this.uploads = JSON.parse(uploadedDocuments);
    }
  }
}
